// pages/analysis/ReportCenter.js —— 分析中心 · 统计报表（doc01 2.12）
// 电量报表 / 收益报表两个子模块，各自区分「汇总 / 单站」；
// 筛选区位于报表区域内部（不在总览之上）：汇总仅「选择时间」，单站为「站点选择 + 选择时间」；
// 返回 / 重置 / 导出；各统计表均含「总」列，汇总的站点报表含「总计」行；
// 单站按并网点分表展示（表头首格为「N#并网点」，首列为日期 / 月份），与 doc01 2.12 文档图一致。
import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Table, Button, Select, Space, message, DatePicker } from 'antd';
import { ReloadOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import PageHead from '../../components/PageHead';
import ScrollTabs from '../../components/ScrollTabs';
import { exportTableToExcel } from '../../utils/exportExcel';
import {
  getEnergyReportMulti, getEnergyReportSingle, getIncomeReportMulti, getIncomeReportSingle,
  getEnergyStationReport, getIncomeStationReport, STATIONS,
} from '../../config/stationData';

const TYPES = [
  { key: 'sharp', label: '尖' }, { key: 'peak', label: '峰' },
  { key: 'flat', label: '平' }, { key: 'valley', label: '谷' },
];

const ENERGY_KEYS = [...TYPES.map((t) => `${t.key}Charge`), 'chargeTotal',
  ...TYPES.map((t) => `${t.key}Discharge`), 'dischargeTotal', 'stationUse'];
const INCOME_KEYS = [...TYPES.map((t) => `${t.key}Cost`), 'costTotal',
  ...TYPES.map((t) => `${t.key}Gain`), 'gainTotal', 'stationCost'];

// 分组表头：时段（尖 / 峰 / 平 / 谷）+「总」列
const periods = (title, suffix, totalIndex) => ({
  title, key: title,
  children: [
    ...TYPES.map((t) => ({ title: t.label, dataIndex: `${t.key}${suffix}`, key: `${t.key}${suffix}`, width: 80 })),
    { title: '总', dataIndex: totalIndex, key: totalIndex, width: 96, accent: true },
  ],
});

// 电量报表列（充电量 / 放电量各含 尖、峰、平、谷、总 五列 + 综合效率）
const energyColumns = (firstCol, pre) => [
  firstCol,
  periods(`${pre}充电量(kWh)`, 'Charge', 'chargeTotal'),
  periods(`${pre}放电量(kWh)`, 'Discharge', 'dischargeTotal'),
  { title: '综合效率(%)', dataIndex: 'eff', key: 'eff', width: 118 },
];

// 收益报表列（充电成本 / 放电成本 各含 尖、峰、平、谷、总 + 站用电成本(总) + 实际收益）
const incomeColumns = (firstCol, pre) => [
  firstCol,
  periods(`${pre}充电成本(元)`, 'Cost', 'costTotal'),
  periods(`${pre}放电成本(元)`, 'Gain', 'gainTotal'),
  { title: `${pre}站用电成本(元)`, dataIndex: 'stationCost', key: 'stationCost', width: 130 },
  { title: '实际收益(元)', dataIndex: 'total', key: 'total', width: 130, accent: true },
];

const sumOf = (rows, keys, label, keyPrefix) => {
  const out = { key: `${keyPrefix}-total`, label };
  keys.forEach((k) => { out[k] = +rows.reduce((a, r) => a + (r[k] || 0), 0).toFixed(1); });
  return out;
};

// 默认统计区间（dayjs，供 RangePicker 取值与 .format() 使用）
const RANGE_PRESET = () => [dayjs('2026-10-06'), dayjs('2026-10-11')];
// 区间兜底：清空 / 非法取值时回落到默认区间，避免取值越界导致渲染异常
const safeRange = (r) => (Array.isArray(r) && r[0] && r[1] ? r : RANGE_PRESET());

// 报表区：汇总 / 单站 + 站点报表 / 日报表 / 月报表 + 筛选 + 表格
const ReportPane = ({ kind }) => {
  const isEnergy = kind === 'energy';
  const [view, setView] = useState('agg');           // agg 汇总 / single 单站
  const [sub, setSub] = useState('station');         // station 站点报表 / day 日报表 / month 月报表
  const [station, setStation] = useState(STATIONS[0].name);
  const [range, setRange] = useState(RANGE_PRESET());
  const [applied, setApplied] = useState({ station: STATIONS[0].name, range: RANGE_PRESET() });

  const onViewChange = (v) => {
    setView(v);
    setSub(v === 'agg' ? 'station' : 'day');
  };

  const doQuery = () => { setApplied({ station, range: safeRange(range) }); message.success('已按条件查询'); };
  const doReset = () => {
    const r = RANGE_PRESET();
    setRange(r); setStation(STATIONS[0].name); setApplied({ station: STATIONS[0].name, range: r });
    message.success('筛选条件已重置');
  };

  const pre = sub === 'month' ? '月' : '日';

  // 数据源
  const rowsOf = () => {
    if (view === 'agg' && sub === 'station') {
      return isEnergy ? getEnergyStationReport() : getIncomeStationReport();
    }
    const mode = sub === 'month' ? 'month' : 'day';
    if (view === 'agg') return isEnergy ? getEnergyReportMulti(mode) : getIncomeReportMulti(mode);
    return isEnergy ? getEnergyReportSingle(mode) : getIncomeReportSingle(mode);
  };
  const rows = rowsOf();

  const labelText = sub === 'month' ? '月份' : '日期';
  const firstTitle = view === 'agg' && sub === 'station' ? '站点名称' : labelText;
  const firstCol = { title: firstTitle, dataIndex: 'label', key: 'label', width: 150, strong: true, fixed: 'left' };

  // 单站：按并网点拆成多张表，表头首格为该并网点名称
  const points = view === 'single' ? Array.from(new Set(rows.map((r) => r.point))) : [];
  const groupOf = (point) => {
    const list = rows.filter((r) => r.point === point);
    const total = sumOf(list, isEnergy ? ENERGY_KEYS : INCOME_KEYS, '总计', `p-${point}`);
    if (isEnergy) total.eff = total.chargeTotal ? +((total.dischargeTotal / total.chargeTotal) * 100).toFixed(1) : 0;
    else total.total = +(total.gainTotal - total.costTotal).toFixed(1);
    return [...list, total];
  };

  const renderTable = (dataSource, firstColumn) => (
    <Table
      rowKey="key"
      dataSource={dataSource}
      size="middle"
      pagination={false}
      scroll={{ x: 1500 }}
      rowClassName={(r) => (String(r.key).includes('total') ? 'rpt-total-row' : '')}
      columns={isEnergy ? energyColumns(firstColumn, pre) : incomeColumns(firstColumn, pre)}
    />
  );

  // 当前表格列（供导出复用）
  const tableColumns = (firstColumn) => (isEnergy ? energyColumns(firstColumn, pre) : incomeColumns(firstColumn, pre));

  // 缺陷 90：导出按钮真正导出 Excel（此前仅弹提示，无文件下载）
  const doExport = () => {
    if (view === 'single') {
      const cols = [{ title: '并网点', dataIndex: 'point', width: 150 }, ...tableColumns({ title: labelText, dataIndex: 'label', width: 110 })];
      exportTableToExcel({ title: `${isEnergy ? '电量报表' : '收益报表'}_单站`, columns: cols, rows });
    } else {
      exportTableToExcel({ title: `${isEnergy ? '电量报表' : '收益报表'}_${firstTitle}`, columns: tableColumns(firstCol), rows });
    }
    message.success('报表已导出 Excel');
  };

  const appliedRange = safeRange(applied.range);

  const filtersAndTable = (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        {view === 'single' ? (
          <>
            <span className="filter-label">站点选择</span>
            <Select value={station} onChange={setStation} style={{ width: 200 }} options={STATIONS.map((s) => ({ value: s.name, label: s.name }))} />
          </>
        ) : null}
        <span className="filter-label">选择时间</span>
        <DatePicker.RangePicker value={range} onChange={setRange} format="YYYY-MM-DD" style={{ width: 250 }} />
        <Button type="primary" icon={<SearchOutlined />} onClick={doQuery}>查询</Button>
        <Button icon={<ReloadOutlined />} onClick={doReset}>重置</Button>
        <Button icon={<DownloadOutlined />} onClick={doExport}>导出</Button>
      </Space>

      {view === 'single' ? points.map((point) => (
        <div key={point} style={{ marginBottom: 16 }}>
          {renderTable(groupOf(point), { title: point, key: point, width: 150, strong: true, children: [{ title: labelText, dataIndex: 'label', key: 'label', width: 110, fixed: 'left' }] })}
        </div>
      )) : renderTable(rows, firstCol)}

      <div className="rpt-caption">
        {view === 'single' ? `站点：${applied.station} · ` : ''}
        统计区间：{appliedRange[0].format('YYYY-MM-DD')} ~ {appliedRange[1].format('YYYY-MM-DD')}
      </div>
    </>
  );

  // 子页切换对齐单站监控胶囊 Tabs（溢出才显示左右箭头）
  return (
    <ScrollTabs
      activeKey={view}
      onChange={onViewChange}
      destroyInactiveTabPane
      items={[
        {
          key: 'agg',
          label: '汇总',
          children: (
            <ScrollTabs
              activeKey={sub}
              onChange={setSub}
              items={[
                { key: 'station', label: '站点报表', children: filtersAndTable },
                { key: 'day', label: '日报表', children: filtersAndTable },
                { key: 'month', label: '月报表', children: filtersAndTable },
              ]}
            />
          ),
        },
        {
          key: 'single',
          label: '单站',
          children: (
            <ScrollTabs
              activeKey={sub === 'station' ? 'day' : sub}
              onChange={setSub}
              items={[
                { key: 'day', label: '日报表', children: filtersAndTable },
                { key: 'month', label: '月报表', children: filtersAndTable },
              ]}
            />
          ),
        },
      ]}
    />
  );
};

const ReportCenter = () => (
  <div>
    <PageHead title="统计报表" desc="电量报表与收益报表，支持汇总 / 单站、日报表 / 月报表与导出" />
    <div className="panel" style={{ padding: '8px 18px 18px' }}>
      <ScrollTabs
        items={[
          { key: 'energy', label: '电量报表', children: <ReportPane kind="energy" /> },
          { key: 'income', label: '收益报表', children: <ReportPane kind="income" /> },
        ]}
      />
    </div>
  </div>
);

export default ReportCenter;
